import { createSlice } from '@reduxjs/toolkit';

export const livingCarbonGrantSlice = createSlice({
  name: 'livingCarbonGrant',
  initialState: {
    currentState: 1,
    steps: [
      {
        icon: 'map',
        label: 'Property Boundary'
      },
      {
        icon: 'map',
        label: 'Requirements'
      },
      {
        icon: 'table',
        label: 'Summary Report'
      },
      {
        icon: 'thumbs-up',
        label: 'Submit'
      }
    ],
    position: [-35.29, 145.25], // Australia
    regionFocus: -1,
    plantingSiteFocus: -1,
    developPlantingSite: -1, // keep track of which planting site is being developed currently
    showEditControl: false,
    drawingFence: -1, // 0 - new fence, 1 - existing fence
    showAddTreeConfirmationModal: false,
    selectedLandParcels: [],
    plantingSites: [],
    fence: [],
    photoMonitoringPoint: [],
    surroundingProperties: [],
    treeMarkers: [],
    species: [],
    groupedSpecies: [],
    searchPlantResultItem: []
  },
  reducers: {
    nextState: state => {
      if (state.currentState < 7) {
        state.currentState += 1;
      }
    },
    prevState: state => {
      if (state.currentState > 1) {
        state.currentState -= 1;
      }
    },
    setPosition: (state, action) => {
      state.position = action.payload;
    },
    setRegionFocus: (state, action) => {
      state.regionFocus = action.payload;
    },
    setPlantingSiteFocus: (state, action) => {
      state.plantingSiteFocus = action.payload;
    },
    setShowEditControl: (state, action) => {
      state.showEditControl = action.payload;
    },
    setDrawingFence: (state, action) => {
      state.drawingFence = action.payload;
    },
    setShowAddTreeConfirmationModal: (state, action) => {
      state.showAddTreeConfirmationModal = action.payload;
    },
    setDevelopPlantingSite: (state, action) => {
      state.developPlantingSite = action.payload;
    },
    setSelectedLandParcels: (state, action) => {
      state.selectedLandParcels = action.payload;
    },
    setSearchPlantResultItem: (state, action) => {
      state.searchPlantResultItem = action.payload;
    },
    addDefinedPropertyBoundary: (state, action) => {
      const parcel = action.payload;
      if (state.selectedLandParcels.find(p => p.id === parcel.id)) {
        state.selectedLandParcels = state.selectedLandParcels.filter(
          p => p.id !== parcel.id
        );
      } else {
        state.selectedLandParcels = [parcel, ...state.selectedLandParcels];
      }
    },
    addPlantingSites: (state, action) => {
      state.plantingSites = [...state.plantingSites, action.payload];
    },
    addFence: (state, action) => {
      state.fence = [...state.fence, action.payload];
    },
    addPhotoMonitoringPoint: (state, action) => {
      state.photoMonitoringPoint = [
        ...state.photoMonitoringPoint,
        action.payload
      ];
    },
    addSurroundingProperties: (state, action) => {
      state.surroundingProperties = [
        ...state.surroundingProperties,
        action.payload
      ];
    },
    addTreeMarkers: (state, action) => {
      state.treeMarkers = [...state.treeMarkers, action.payload];
    },
    addSpecies: (state, action) => {
      state.species = [...state.species, action.payload];
    },
    addGroupSpecies: (state, action) => {
      let newSpecies = action.payload;
      newSpecies.forEach(newSpecie => {
        const siteId = newSpecie.siteId;

        // Find the index of the grouped species with the same siteId
        const index = state.groupedSpecies.findIndex(s => s.siteId === siteId);

        if (index !== -1) {
          // If a grouped species with the same name exists, check and update based on name
          const existingGroupedSpecies = state.groupedSpecies[index];
          const specieIndex = existingGroupedSpecies.species.findIndex(
            specie => specie.name === newSpecie.name
          );

          if (specieIndex !== -1) {
            // If specie with the same sideId exists, update it
            existingGroupedSpecies.species[specieIndex] = newSpecie;
          } else {
            // If specie with the same sideId doesn't exist, add it to the existing grouped species
            existingGroupedSpecies.species.push(newSpecie);
          }
        } else {
          // If no grouped species with the same siteId exists, create a new one
          const groupedSpecies = {
            id: siteId,
            siteId: siteId,
            species: [newSpecie]
          };
          state.groupedSpecies.push(groupedSpecies);
        }
      });
    },
    removeFence: (state, action) => {
      state.fence = state.fence.filter(p => p.id !== action.payload);
    },
    reset: state => ({
      ...state,
      currentState: 1,
      drawingFence: false,
      selectedLandParcels: [],
      plantingSites: [],
      fence: []
    })
  }
});

export const {
  nextState,
  prevState,
  setPosition,
  setRegionFocus,
  setPlantingSiteFocus,
  setShowEditControl,
  setDrawingFence,
  setShowAddTreeConfirmationModal,
  setDevelopPlantingSite,
  setSearchPlantResultItem,
  addDefinedPropertyBoundary,
  addPlantingSites,
  addFence,
  addPhotoMonitoringPoint,
  addSurroundingProperties,
  addTreeMarkers,
  addSpecies,
  addGroupSpecies,
  removeFence,
  reset
} = livingCarbonGrantSlice.actions;

export default livingCarbonGrantSlice.reducer;
